import { useEffect, useMemo } from 'react';
import { useMutation } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { FormPageLayout } from '@/components/layout/form-page-layout';
import { usePageHeader } from '@/contexts/PageHeaderContext';
import { useGo } from '@/hooks/useResourceNavigation';
import { extractCheckResultError } from '@/lib/checkResult';
import {
	getMutationOptions,
	showMutationErrorToast,
	showMutationSuccessToast
} from '@/lib/mutationRefetch';
import { VoucherDiscountTypeEnum } from '@/graphql/generated';

import { VoucherForm, type VoucherFormSubmitData } from './voucher-form';
import { VOUCHER_CREATE_MUTATION } from './vouchers.gql.tsx';
import {
	buildCreateVoucherInput,
	generateIdempotentVoucherId
} from './voucher-input';
import { useVoucherDiscountTypeOptions } from './server-data-hooks';

interface CloneData {
	voucherId?: string; // Pre-generated ID from clone function
	name?: string | null;
	code?: string; // Pre-generated unique code from clone function
	isAdHoc?: boolean;
	isCustomerSpecific?: boolean;
	discountValue?: number | null;
	maxDiscountAmount?: number | null;
	freeTimeMinutes?: number | null;
	usageLimit?: number | null;
	validUntilDate?: string;
	isActive?: boolean;
	voucherDiscountTypeEnum?: VoucherDiscountTypeEnum;
	campaignId?: string | null;
	customerId?: string | null;
	facilityId?: string | null;
	rateId?: string | null;
	rateBindingRequired?: boolean;
	// Display data for autocomplete initial labels
	campaign?: { campaignId: string; name: string } | null;
	customer?: { customerId: string; firmName?: string | null; email?: string | null } | null;
	rate?: { rateId: string; name?: string | null } | null;
}

interface LocationState {
	cloneData?: CloneData;
}

export const VouchersCreate = () => {
	const { t } = useTranslation();
	const go = useGo();
	const location = useLocation();
	const cloneData = (location.state as LocationState | null)?.cloneData;
	const { setPageHeader, resetPageHeader } = usePageHeader();

	// Use the pre-generated voucherId from clone data, or generate a new one
	const voucherId = useMemo(
		() => cloneData?.voucherId ?? generateIdempotentVoucherId(),
		[cloneData?.voucherId]
	);

	const { voucherDiscountTypeOptions, loading: voucherDiscountTypesLoading } =
		useVoucherDiscountTypeOptions();

	const [createVoucher, { loading: isSubmitting }] = useMutation(
		VOUCHER_CREATE_MUTATION
	);

	useEffect(() => {
		setPageHeader({
			title: t('vouchers:create.title'),
			description: t('vouchers:create.description'),
			showBackButton: true,
			onBackClick: () => go({ to: '/vouchers' })
		});

		return () => {
			resetPageHeader();
		};
	}, [go, resetPageHeader, setPageHeader, t]);

	const handleSubmit = async (values: VoucherFormSubmitData) => {
		const payload = buildCreateVoucherInput(values, voucherId);

		try {
			const { data } = await createVoucher(
				getMutationOptions('vouchers', { input: payload })
			);

			const message = extractCheckResultError(data?.createVoucher);
			if (message) {
				showMutationErrorToast('vouchers', 'create', { message });
				return;
			}

			showMutationSuccessToast('vouchers', 'create');
			// Navigate back with the newly created voucher ID for selection
			go({
				to: '/vouchers',
				state: { selectVoucherId: voucherId }
			});
		} catch (error) {
			showMutationErrorToast('vouchers', 'create', { error });
		}
	};

	// Show loading while required data loads
	if (voucherDiscountTypesLoading) {
		return (
			<FormPageLayout>
				<div className="flex items-center justify-center p-8">
					<div className="text-center">
						<div className="w-8 h-8 mx-auto mb-4 border-4 rounded-full animate-spin border-primary border-t-transparent"></div>
						<p className="text-muted-foreground">
							{t('vouchers:messages.loading')}
						</p>
					</div>
				</div>
			</FormPageLayout>
		);
	}

	return (
		<FormPageLayout>
			<VoucherForm
				key="create-voucher-form"
				mode={cloneData ? 'clone' : 'create'}
				initialData={cloneData ? {
					name: cloneData.name ?? '',
					code: cloneData.code ?? '', // Use pre-generated unique code
					isAdHoc: cloneData.isAdHoc ?? false,
					isCustomerSpecific: cloneData.isCustomerSpecific ?? false,
					customerId: cloneData.customerId ?? null,
					rateBindingRequired: cloneData.rateBindingRequired ?? false,
					rateId: cloneData.rateId ?? null,
					discountValue: cloneData.discountValue ?? 0,
					maxDiscountAmount: cloneData.maxDiscountAmount ?? null,
					freeTimeMinutes: cloneData.freeTimeMinutes ?? null,
					usageLimit: cloneData.usageLimit ?? null,
					isActive: cloneData.isActive ?? true,
					validUntilDate: cloneData.validUntilDate ?? '',
					voucherDiscountTypeEnum: cloneData.voucherDiscountTypeEnum ?? VoucherDiscountTypeEnum.TariffReplacement,
					campaignId: cloneData.campaignId ?? null,
					// Display data for autocomplete labels
					campaign: cloneData.campaign,
					customer: cloneData.customer,
					rate: cloneData.rate
				} : undefined}
				voucherDiscountTypeOptions={voucherDiscountTypeOptions}
				onSubmit={handleSubmit}
				isSubmitting={isSubmitting}
				headerTitle={t('vouchers:create.title')}
			/>
		</FormPageLayout>
	);
};
